import { TextButton } from '@approvalmax/ui';
import { Box } from '@approvalmax/ui/src/components';
import { intl } from '@approvalmax/utils';
import { ConfirmationFullIcon, FailedListIcon, ToastRejectedIcon } from 'modules/sprites';
import moment from 'moment';
import { ReactNode } from 'react';
import { noValue } from 'shared/constants';
import { GetQBooksBudgetsImpact } from 'shared/data/webApp/v2';
import { AccountingDomainEntitiesBudgetCheckStatus, BudgetsQBooksDimensionType } from 'shared/data/webApp/v2';

import { ColumnDefinition } from '../../../selectors/types/ColumnDefinition';
import { messages } from './QBooksBudgetingSection.messages';
import { CustomLabels } from './QBooksBudgetingSection.types';

export const getColumnDefinitions = (
    currency: string,
    customLabels: CustomLabels,
    openBudgetImpactDetailsPopup: (item: GetQBooksBudgetsImpact['response']['budgetImpacts'][number]) => void
): ColumnDefinition<GetQBooksBudgetsImpact['response']['budgetImpacts'][number]>[] => {
    return [
        {
            id: 'budgetName',
            name: messages.budgetName,
            value: (item) => item.budgetName,
            cell: (_, item) => (
                <TextButton
                    colorTheme='green'
                    size='small1'
                    execute={() => openBudgetImpactDetailsPopup(item)}
                    upperCased={false}
                >
                    {item.budgetName}
                </TextButton>
            ),
        },
        {
            id: 'class',
            name: customLabels.class ?? messages.class,
            value: (item) => {
                if (item.subdividedBy?.type === BudgetsQBooksDimensionType.class) {
                    return item.subdividedBy.value ?? noValue;
                }

                return noValue;
            },
        },
        {
            id: 'location',
            name: customLabels.location ?? messages.location,
            value: (item) => {
                if (item.subdividedBy?.type === BudgetsQBooksDimensionType.location) {
                    return item.subdividedBy.value ?? noValue;
                }

                return noValue;
            },
        },
        {
            id: 'customer',
            name: customLabels.customer ?? messages.customer,
            value: (item) => {
                if (item.subdividedBy?.type === BudgetsQBooksDimensionType.customer) {
                    return item.subdividedBy.value ?? noValue;
                }

                return noValue;
            },
        },
        {
            id: 'category',
            name: messages.category,
            value: (item) => item.accountName,
        },
        {
            id: 'budgetPeriod',
            name: messages.budgetPeriod,
            value: (item) =>
                `${moment.utc(item.budgetPeriodStart.asDateTime).format('MMM YYYY')} - ${moment.utc(item.budgetPeriodEnd.asDateTime).format('MMM YYYY')}`,
        },
        {
            id: 'remainingBudget',
            name: messages.remainingBudget({ currency }),
            value: (item) => intl.formatNumber(item.budgetAmounts.remainingAmount),
            alignRight: true,
        },
        {
            id: 'amount',
            name: messages.amount({ currency }),
            value: (item) => intl.formatNumber(item.linesAmount),
            alignRight: true,
        },
        {
            id: 'status',
            name: messages.status,
            hideTitle: true,
            value: (item) => item.budgetCheckStatus,
            cell: (_, item) => {
                let icon: ReactNode = null;

                switch (item.budgetCheckStatus) {
                    case AccountingDomainEntitiesBudgetCheckStatus.Ok:
                        icon = <ConfirmationFullIcon width={20} height={20} />;

                        break;

                    case AccountingDomainEntitiesBudgetCheckStatus.Warning:
                        icon = <ToastRejectedIcon width={20} height={20} />;

                        break;

                    case AccountingDomainEntitiesBudgetCheckStatus.Overbudget:
                        icon = <FailedListIcon width={20} height={20} />;

                        break;
                }

                return <Box spacing='0 0 0 8'>{icon}</Box>;
            },
        },
    ];
};
